import {
    Breadcrumbs,
    Button,
    ClickAwayListener,
    Link,
    Stack,
    StepIconProps,
    TextField,
    Typography,
} from '@mui/material';
import Step from '@mui/material/Step';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { styled } from '@mui/material/styles';
import { useOktaAuth } from '@okta/okta-react';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import DataSourceType from '../DataSourceType';
import Util from '../Util';
import Backend from '../backend/Backend';
import DateSelectionDialog from '../components/DateSelectionDialog';
import ValidationRectangle from '../components/ValidationRectangle';
import { ReactComponent as ConfigEditIcon } from '../resources/images/edit_config.svg';
import AquantWarningIcon from '../resources/images/process_step_warning.svg';
import stepSelectGoalActiveIcon from '../resources/images/step_select_goal_active.svg';
import stepSelectGoalCompleteIcon from '../resources/images/step_select_goal_complete.svg';
import stepSelectGoalNotCompleteIcon from '../resources/images/step_select_goal_not_complete.svg';
import { ConfigStore } from '../stores/ConfigStore';
import { WizardStore } from '../stores/WizardStore';
import { useTelemetry } from '../tools/Telemetry';
import './Create.scss';
import { DataSettings, getDataSettingsConfirmProps } from './DataSettings';
import DataSource from './DataSource';
import { goals_list } from './Goals';
import StepConfirmation, { ConfirmationDialogProps, defaultConfirmationDialogProps } from './StepConfirmation';
import Summary from './Summary';
import Wizard from './Wizard';

const CreateStepConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundImage:
                'linear-gradient(360deg, #3775FC -12.06%, rgba(55, 117, 252, 0.0001) 17.17%, rgba(55, 117, 252, 0.0001) 74.83%, #3775FC 111.38%)',
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            border: '1px solid rgba(169, 221, 237, 1)',
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        border: '1px solid rgba(169, 221, 237, 1)',
    },
}));

const CreateStepIconRoot = styled('div')<{
    ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
    zIndex: 1,
    width: 32,
    height: 32,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...(ownerState.active && {}),
    ...(ownerState.completed && {}),
}));

function CreateStepIcon(props: StepIconProps) {
    const { active, completed, className } = props;

    return (
        <CreateStepIconRoot ownerState={{ completed, active }} className={className}>
            {active ? (
                <img alt="" src={stepSelectGoalActiveIcon} />
            ) : completed ? (
                <img alt="" src={stepSelectGoalCompleteIcon} />
            ) : (
                <img alt="" src={stepSelectGoalNotCompleteIcon} />
            )}
        </CreateStepIconRoot>
    );
}

const getSaveConfirmProps = (saveConfig: () => void, skipConfirm: () => void) => {
    const res: ConfirmationDialogProps[] = [];

    const saveConfirmProps: ConfirmationDialogProps = {
        ...defaultConfirmationDialogProps,
        title: 'Are you sure?',
        content: (
            <>
                <div style={{ display: 'flex', marginBottom: 15 }}>
                    <img src={AquantWarningIcon} alt="warning-icon" style={{ marginRight: 5 }} />
                    <Typography variant="subtitle2">Changes to the configuration haven't been saved</Typography>
                </div>
                <Typography variant="body1">
                    To automatocally retreive these last settings, save your changes
                </Typography>
            </>
        ),
        onYes: saveConfig,
        yesLabel: 'Save',
        onClose: skipConfirm,
        closeLabel: 'Skip',
    };

    res.push(saveConfirmProps);

    return res;
};

interface CreateProps extends RouteComponentProps {
    progress: Function;
    configStore: ConfigStore;
    wizardStore: WizardStore;
    isEdit: boolean;
}

interface stateType {
    expanded: string;
    config: any;
    wizardType: string;
    partialMode?: 'Add new data' | 'Replace latest file';
}

export const WizardTypes = {
    Create: 'Create',
    RefreshData: 'RefreshData',
    EditDataSettings: 'EditDataSettings',
    CsvPartial: 'CsvPartial',
};

export const WizardSteps = {
    Goal: 0,
    DataSource: 1,
    DataSetting: 2,
    Summary: 3,
};

const ConfigNameTextField = styled(TextField)({
    borderBottom: '1px solid #DADCDF',
    width: '230px',
});

const CreateHeader = (props: {
    configName: string;
    setConfiguratioName: Function;
    currentStepTitle: string;
    saveConfig?: any;
}) => {
    const { t } = useTranslation();

    const { configName, setConfiguratioName, currentStepTitle, saveConfig } = props;
    const [configNameEditModeOn, toggleConfigNameEditMode] = useState<boolean>(false);
    const [tempConfigName, setTempConfigName] = useState<string>('');
    const currentTenant = useRef(
        Util.getSessionCurrentTenantInfo() && Util.getSessionCurrentTenantInfo().id
            ? Util.getSessionCurrentTenantInfo()
            : Util.getCurrentTenantInfo(),
    );

    useEffect(() => {
        if (!configName || configName.length === 0) {
            const newName = Util.generateNewConfigName();
            setTempConfigName(newName);
            setConfiguratioName(newName);
        } else {
            setTempConfigName(configName);
        }
    }, [setConfiguratioName, configName]);

    const renderLogoAndConfigName = () => {
        const handleApprove = () => {
            setConfiguratioName(tempConfigName);
            toggleConfigNameEditMode(false);
        };

        const handleCancel = () => {
            setTempConfigName(configName);
            toggleConfigNameEditMode(false);
        };

        return (
            <Breadcrumbs className="Breadcrumbs">
                {currentTenant.current.logo !== 'null' ? (
                    <img src={currentTenant.current.logo} className="BreadcrumbsLogo" alt="tenant-logo" />
                ) : (
                    <Typography className="BreadcrumbsTypo" component="div">
                        {currentTenant.current.name}
                    </Typography>
                )}
                <Stack direction="row">
                    {configNameEditModeOn ? (
                        <ClickAwayListener onClickAway={() => configNameEditModeOn && handleCancel()}>
                            <div className="ConfigNameEdit">
                                <ConfigNameTextField
                                    size="small"
                                    placeholder={t('wizard.data-source.config-name')}
                                    onChange={(e) => setTempConfigName(e.target.value)}
                                    value={tempConfigName}
                                    variant="standard"
                                    InputProps={{
                                        disableUnderline: true,
                                        classes: {
                                            input:
                                                tempConfigName.length > 0
                                                    ? 'ConfigNameTextField'
                                                    : 'ConfigNameTextFieldPlaceholder',
                                        },
                                    }}
                                />
                                <ValidationRectangle
                                    onApprove={() => handleApprove()}
                                    onCancel={() => handleCancel()}
                                    disabled={tempConfigName.length === 0}
                                />
                            </div>
                        </ClickAwayListener>
                    ) : (
                        <>
                            <Typography
                                className="BreadcrumbsTypo"
                                component="div"
                                onClick={() => toggleConfigNameEditMode(true)}
                            >
                                {configName}
                            </Typography>
                            <ConfigEditIcon
                                onClick={() => {
                                    toggleConfigNameEditMode(true);
                                }}
                                color="#2468FF"
                                style={{ cursor: 'pointer' }}
                            />
                        </>
                    )}
                </Stack>
            </Breadcrumbs>
        );
    };

    return (
        <Stack direction="row" className="Header">
            <Stack alignItems="center" direction="row">
                <Typography component="div" className="StepTitle">
                    {currentStepTitle}
                </Typography>
                {saveConfig ? (
                    <Button
                        className="HeaderSaveBtn"
                        onClick={async () => {
                            await saveConfig();
                        }}
                    >
                        Save
                    </Button>
                ) : null}
            </Stack>
            {renderLogoAndConfigName()}
        </Stack>
    );
};

const Create: FunctionComponent<CreateProps> = observer((props: CreateProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { trackEvent } = useTelemetry('Create', { action: '', goal_name: '', data_source: '' });
    const { authState, oktaAuth } = useOktaAuth();
    const { configStore } = props;
    const { wizardStore } = props;

    // Project level
    const { source_type, setSourceType } = configStore;
    const { selected_goal, setSelectedGoal } = configStore;
    const { config_name, setConfigName } = configStore;
    const { parent_config_id, setParentConfigId } = configStore;
    const { data_processing_config_id, setDataProcessingConfigId } = configStore;
    const { createConfig, handleRunTemplateTest, saveDataSource, resetConfigData } = configStore;

    // Data Source
    const { sourceCreds } = configStore;
    const { masterFiles, setMasterFiles } = configStore;
    const { lookupConfig, setLookupConfig } = configStore;

    // Data Settings
    const { dataSettings, getDataSettings } = configStore;
    const { template_json, setTemplateJson } = configStore;
    const { template_config, setTemplateConfig } = configStore;

    // Summary
    const { task_id, setNewTask } = configStore;
    const { startDate, setStartDate } = configStore;
    const { endDate, setEndDate } = configStore;

    // Wizard
    const { activeStep, setActiveStep } = wizardStore;
    const [stepConfirmation, setStepConfirmation] = useState(false);
    const [confirmationDialogs, setConfirmationDialogs] = useState<ConfirmationDialogProps[]>();
    const { getMultipleAssignments, mappingValid, saveAssignments, clearAssignment, cancelAssignments } = wizardStore;
    const { loadedConfigMultipleAssignments, setLoadedConfigMultipleAssignments } = wizardStore;
    const { processDialogOpen, setProcessDialogOpen } = configStore;
    const { openDateSelection, setOpenDateSelection } = wizardStore;
    const processButton = useRef<HTMLButtonElement>();
    const { templateTestRun, setTemplateTestRun } = configStore;

    const handleOnSubmitDatesDialog = async (startDate: Date, endDate: Date) => {
        setOpenDateSelection(false);
        setStartDate(startDate);
        setEndDate(endDate);
        if (templateTestRun) {
            props.progress(true);
            await handleRunTemplateTest(
                oktaAuth,
                () => {
                    setActiveStep(activeStep + 1);
                    props.progress(false);
                },
                () => props.progress(false),
            );
        } else {
            setProcessDialogOpen(true, () => setActiveStep(activeStep + 1));
        }
    };

    useEffect(() => {
        if (!authState) {
            return;
        }

        if (props.isEdit && parent_config_id === '' && data_processing_config_id === '') {
            return;
        }

        if (!authState.isAuthenticated) {
            setTemplateJson(null);
            setTemplateConfig(null);
        } else if (!props.isEdit) {
            props.progress(true);
            Backend.getConfig(oktaAuth, parent_config_id).then((config) => {
                const { data } = config;
                if (data) {
                    const config = data;
                    let position = 0;
                    for (let i = 0; i < config.environments.length; i++) {
                        if (config.environments[i]?.template?.mapping_raw_json) {
                            position = i;
                            break;
                        }
                    }
                    const [targetObj] = config.environments.splice(position, 1);
                    config.environments.unshift(targetObj);
                    if (lookupConfig) {
                        const mappingRawJson = JSON.parse(config.environments[0].template.mapping_raw_json);
                        mappingRawJson.lookupObjects = lookupConfig;
                        config.environments[0].template.mapping_raw_json = JSON.stringify(mappingRawJson, null, '\t');
                        config.environments[0].template.mapping = mappingRawJson;
                    }
                    setTemplateJson(config.environments[0].template.mapping_raw_json);
                    setTemplateConfig(config);
                    props.progress(false);
                }
            });
        }

        if (!dataSettings) {
            (async function () {
                props.progress(true);
                await getDataSettings(oktaAuth, () => {
                    props.progress(false);
                    setLoadedConfigMultipleAssignments(getMultipleAssignments());
                });
            })();
        }

        if (!selected_goal) {
            setSelectedGoal(goals_list[0]);
        }
    }, [parent_config_id, data_processing_config_id, props.isEdit]);

    const steps = [
        // { label: t("new-config.goal"), icon: stepSelectGoalActiveIcon },
        { label: t('new-config.source'), icon: stepSelectGoalActiveIcon, headerTitle: t('wizard.title') },
        // { label: t("new-config.import"), icon : stepSelectGoalActiveIcon },
        { label: t('new-config.check'), icon: stepSelectGoalActiveIcon, headerTitle: t('data-settings.settings') },
        { label: t('new-config.summary'), icon: stepSelectGoalActiveIcon, headerTitle: t('summary.header') },
    ];

    const canAdvanceDataSource = () => {
        const baseCondition = !!(
            source_type &&
            template_config &&
            template_config.environments &&
            template_config.environments[0] &&
            template_config.config_type === source_type.name
        );

        if (baseCondition) {
            if (
                (source_type.name === DataSourceType.svmx ||
                    source_type.name === DataSourceType.sfdc ||
                    source_type.name === DataSourceType.custom) &&
                (sourceCreds || props.isEdit)
            ) {
                return true;
            }
            if (
                source_type.name === DataSourceType.csv &&
                ((masterFiles && masterFiles.length !== 0) || props.isEdit)
            ) {
                return true;
            }

            return false;
        }

        return false;
    };

    const canAdvance = (isSaveForLater: boolean = false) => {
        if (activeStep === WizardSteps.Goal && !selected_goal) {
            return false;
        }

        if (activeStep === WizardSteps.DataSource && !canAdvanceDataSource()) {
            return false;
        }

        if (activeStep === WizardSteps.DataSetting && !isSaveForLater && !mappingValid()) {
            return false;
        }

        return true;
    };

    const create_config_and_save = async () => {
        await createConfig(oktaAuth);
        await saveDataSource(oktaAuth);
    };

    const handleNext = async (saveForLater?: boolean) => {
        const trackingData = { data: { action: 'Create/Next', goal_name: null, data_source: null } };

        if (saveForLater) {
            props.progress(true);
            await create_config_and_save();
            props.progress(false);
            history.push('/');
            configStore.resetConfigData();
            return;
        }

        if (activeStep === WizardSteps.Goal) {
            // goals
            trackingData.data.goal_name = selected_goal.trackerId;
        } else if (activeStep === WizardSteps.DataSource) {
            // data source
            trackingData.data.data_source = source_type.name as any;
            const mappingJson = JSON.parse(template_json);
            const template = {
                mapping: mappingJson,
                mapping_raw_json: JSON.stringify(mappingJson, null, '\t'),
            };
            template_config.data_processing_config = dataSettings;
            template_config.environments[0].template = template;

            setTemplateConfig({ ...template_config });
        }

        if (trackingData.data.goal_name || trackingData.data.data_source) {
            trackEvent(trackingData);
        }

        if (activeStep === WizardSteps.DataSetting) {
            const stepConfirmationProps = getDataSettingsConfirmProps
                ? getDataSettingsConfirmProps(
                      loadedConfigMultipleAssignments,
                      mappingValid,
                      handleNext,
                      getMultipleAssignments,
                      clearAssignment,
                      saveAssignments,
                      cancelAssignments,
                  )
                : undefined;

            setConfirmationDialogs(stepConfirmationProps);

            if (stepConfirmationProps.length > 0) {
                setStepConfirmation(true);
                return;
            }

            if (
                source_type.name === DataSourceType.svmx ||
                source_type.name === DataSourceType.sfdc ||
                source_type.name === DataSourceType.custom
            ) {
                setOpenDateSelection(true);
            } else if (source_type.name === DataSourceType.csv) {
                setProcessDialogOpen(true, () => setActiveStep(activeStep + 1));
            }
        } else if (activeStep === WizardSteps.Summary) {
            configStore.resetConfigData();
            history.push('/configurations'); // user can close the window at this point
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep === WizardSteps.DataSource || props.isEdit) {
            const stepConfirmationProps = getSaveConfirmProps(
                async () => {
                    handleNext(true);
                },
                () => {
                    history.push('/');
                    configStore.resetConfigData();
                },
            );
            setConfirmationDialogs(stepConfirmationProps);

            if (activeStep === WizardSteps.DataSource && !canAdvanceDataSource()) {
                configStore.resetConfigData();
                history.push('/');
                configStore.resetConfigData();
                return;
            }

            if (stepConfirmationProps.length > 0) {
                setStepConfirmation(true);
            }
        } else {
            setActiveStep(activeStep - 1);
        }
    };

    let nextButtonLabel = '';
    if (activeStep === WizardSteps.DataSource) {
        nextButtonLabel = t('common.configure-data');
    } else if (activeStep === WizardSteps.DataSetting) {
        nextButtonLabel = t('common.process-data');
    } else if (activeStep === WizardSteps.Summary) {
        nextButtonLabel = t('wizard.see-all-proj');
    } else {
        nextButtonLabel = t('common.next'); // default
    }

    return (
        <>
            <Stack className="Create" spacing={2} direction="column" sx={{ margin: 0, paddingBottom: '40px' }}>
                {props.isEdit ? <Stack marginTop="16px" sx={{ backgroundColor: '#F0F2F5' }} /> : null}
                {!props.isEdit && (
                    <div className="StepperRoot">
                        <Stepper className="Stepper" activeStep={activeStep} connector={<CreateStepConnector />}>
                            {steps.map((step, index) => (
                                <Step key={index}>
                                    <div>
                                        <StepLabel
                                            classes={
                                                index + 1 === activeStep
                                                    ? { label: 'ActiveStep' }
                                                    : index + 1 < activeStep
                                                    ? { label: 'CompletedStep' }
                                                    : { label: 'NotCompletedStep' }
                                            }
                                            StepIconComponent={CreateStepIcon}
                                        >
                                            {step.label}
                                        </StepLabel>
                                    </div>
                                </Step>
                            ))}
                        </Stepper>
                    </div>
                )}
                <CreateHeader
                    saveConfig={activeStep === WizardSteps.DataSetting ? create_config_and_save : null}
                    configName={config_name}
                    setConfiguratioName={setConfigName}
                    currentStepTitle={steps[activeStep - 1].headerTitle}
                />

                {/* {activeStep === 0 && <Goals selectedGoal={selected_goal} onGoalSelected={(goal: any) => (setSelectedGoal(goal))} />} */}
                {activeStep === WizardSteps.DataSource && <DataSource wizardStore={wizardStore} />}

                {activeStep === WizardSteps.DataSetting && dataSettings ? (
                    <DataSettings wizardStore={wizardStore} />
                ) : null}

                {activeStep === WizardSteps.Summary && <Summary {...props} configStore={configStore} />}

                <Stack
                    className="NavActions"
                    direction="row"
                    justifyContent={activeStep === WizardSteps.Summary ? 'center' : 'space-between'}
                    alignItems="center"
                    spacing={2}
                >
                    {activeStep !== WizardSteps.Summary && (
                        <Button className="Back" onClick={handleBack}>{`< ${t('common.back')}`}</Button>
                    )}
                    <Stack direction="row">
                        {(activeStep === WizardSteps.DataSetting || activeStep === WizardSteps.DataSource) &&
                        (canAdvance(true) || props.isEdit) ? (
                            <Link
                                sx={{ textDecoration: 'none' }}
                                className="SaveForLater"
                                onClick={() => {
                                    handleNext(true);
                                }}
                            >
                                {t('common.save-for-later')}
                            </Link>
                        ) : null}
                        {activeStep !== WizardSteps.Summary || (activeStep === WizardSteps.Summary && task_id) ? (
                            <Button
                                data-testid="configureDataNextBtn"
                                disabled={!canAdvance()}
                                className="Next"
                                ref={processButton}
                                variant="contained"
                                disableRipple
                                onClick={() => {
                                    handleNext(false);
                                }}
                            >
                                {nextButtonLabel}
                                {(activeStep === WizardSteps.DataSetting || activeStep === WizardSteps.DataSource) && (
                                    <span className="NextBtnArrows">{'>>'}</span>
                                )}
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>
                {stepConfirmation && confirmationDialogs ? (
                    <StepConfirmation
                        confirmationDialogs={confirmationDialogs}
                        finish={() => {
                            setStepConfirmation(false);
                        }}
                        cancel={() => setStepConfirmation(false)}
                    />
                ) : null}
            </Stack>
            <DateSelectionDialog
                config={template_config}
                open={openDateSelection}
                onClose={() => {
                    setOpenDateSelection(false);
                }}
                onSubmit={(startDate: any, endDate: any) => {
                    handleOnSubmitDatesDialog(startDate, endDate);
                }}
                actionTitle={templateTestRun ? 'Test template' : 'Process data'}
                actionDescription={templateTestRun ? 'template test' : 'data process'}
            />
        </>
    );
});

interface CreateWizardProps extends RouteComponentProps {
    configStore: ConfigStore;
}

const CreateWizard: FunctionComponent<CreateWizardProps> = observer((props: CreateWizardProps) => {
    const wizardStore = useMemo(() => new WizardStore(props.configStore), [props.configStore]);
    const { showProgress, setPartialMode } = wizardStore.configStore;
    const location = useLocation<stateType>();

    useEffect(() => {
        autorun(() => {
            if (location.state) {
                wizardStore.setExpandedTab(location.state.expanded);

                switch (location.state.wizardType) {
                    case WizardTypes.Create:
                        wizardStore.setCreateWizardSteps();
                        break;

                    case WizardTypes.EditDataSettings:
                        wizardStore.setEditDataSettingsSteps();
                        break;

                    case WizardTypes.CsvPartial:
                        wizardStore.setCsvPartialSteps();
                        break;

                    default:
                        break;
                }
            }
        });
    }, [location, wizardStore]);

    useEffect(() => {
        if (location.state?.config) {
            props.configStore.resetConfigData(); // reset state before loading data
            props.configStore.setParentConfigId(location.state.config.rowid);
            props.configStore.setSourceType({ name: location.state.config.config_type });
            props.configStore.setConfigName(location.state.config.config_name);
            props.configStore.setDataProcessingConfigId(
                location.state.config.environments[0].data_processing_config_id,
            );
            props.configStore.setTemplateConfig(location.state.config);
            props.configStore.setTemplateJson(location.state.config.environments[0].template.mapping_raw_json);
        }

        if (location.state?.partialMode) {
            props.configStore.setPartialMode(location.state.partialMode);
        }
    }, [location]);

    return (
        <>
            <Wizard {...props} wizardStore={wizardStore} progress={showProgress} isEdit={!!location.state?.config} />
            {/* <Create {...props} wizardStore={wizardStore} progress={showProgress} isEdit={location.state.config ? true : false} /> */}
        </>
    );
});

export default CreateWizard;
